<script setup>
import { shallowRef } from 'vue'
import ClassicAuthCard from '../components/ClassicAuthCard.vue'

const props = defineProps({
  identifier: { type: String, required: true },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  registerUrl: { type: String, default: '' },
  termsLink: { type: String, default: '' },
  privacyLink: { type: String, default: '' },
  isAdmin: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'forgot', 'phone'])
const email = shallowRef(props.identifier)
const password = shallowRef('')
const passwordVisible = shallowRef(false)

function submit() {
  if (!email.value.trim() || !password.value || props.loading) return
  emit('submit', {
    identifier: email.value.trim(),
    password: password.value,
  })
}
</script>

<template>
  <ClassicAuthCard
    :title="isAdmin ? 'Admin Login' : 'Login'"
    :is-admin="isAdmin"
    :register-url="registerUrl"
  >
    <form class="classic-form" :class="{ 'classic-form--admin': isAdmin }" @submit.prevent="submit">
      <label class="classic-form__label" for="classic-email">
        <img :src="isAdmin ? '/images/admin/email.png' : '/images/icons/email.png'" alt="">
        Email *
      </label>
      <input
        id="classic-email"
        v-model="email"
        class="classic-form__input"
        type="email"
        autocomplete="username"
        required
      >

      <label class="classic-form__label classic-form__label--password" for="classic-password">
        <img :src="isAdmin ? '/images/admin/password.png' : '/images/icons/password.png'" alt="">
        Password *
      </label>
      <div class="classic-form__password">
        <input
          id="classic-password"
          v-model="password"
          class="classic-form__input"
          :class="{ 'classic-form__input--invalid': error }"
          name="password"
          :type="passwordVisible ? 'text' : 'password'"
          autocomplete="current-password"
          required
        >
        <button
          class="classic-form__password-toggle"
          type="button"
          :aria-label="passwordVisible ? 'Hide password' : 'Show password'"
          @click="passwordVisible = !passwordVisible"
        >
          <img class="auth-login__classic-password-eye" src="/images/icons/eye.png" alt="">
        </button>
      </div>
      <p v-if="error" class="classic-form__error" role="alert">{{ error }}</p>

      <div class="classic-form__links">
        <button class="classic-form__link" type="button" :disabled="loading" @click="emit('forgot', email.trim())">
          Forgot Password?
        </button>
        <button
          v-if="!isAdmin"
          class="classic-form__link"
          type="button"
          :disabled="loading"
          @click="emit('phone')"
        >
          Login with mobile number
        </button>
      </div>

      <p v-if="!isAdmin" class="classic-form__terms">
        By continuing, you agree with the
        <a v-if="termsLink" :href="termsLink" target="_blank" rel="noopener">Terms of Service</a>,
        acknowledge the
        <a v-if="privacyLink" :href="privacyLink" target="_blank" rel="noopener">Privacy Policy</a>
        and agree to receive text messages from
        <a href="https://remonihealth.com/" target="_blank" rel="noopener">RemoniHealth</a>.
      </p>

      <button class="classic-form__submit" type="submit" :disabled="loading || !email || !password">
        {{ loading ? 'Signing in…' : 'Login' }}
      </button>
    </form>
  </ClassicAuthCard>
</template>

<style scoped>
.classic-form {
  display: flex;
  flex-direction: column;
}

.classic-form__label {
  align-items: center;
  color: #212529;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  margin-bottom: 8px;
}

.classic-form__label--password {
  margin-top: 16px;
}

.classic-form__label img {
  height: 20px;
  object-fit: contain;
  width: 20px;
}

.classic-form__input {
  background: transparent;
  border: 0;
  border-bottom: 1px solid #c6c6c6;
  border-radius: 0;
  box-sizing: border-box;
  color: #212529;
  font: inherit;
  min-height: 46px;
  padding: 10px 0;
  width: 100%;
}

.classic-form__input:focus {
  border-color: #564495;
  box-shadow: none;
  outline: none;
}

.classic-form__input--invalid {
  border-color: #dc3545;
}

.classic-form__password {
  position: relative;
}

.classic-form__password .classic-form__input {
  padding-right: 34px;
}

.classic-form__password-toggle {
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 4px;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.classic-form__error {
  color: #dc3545;
  font-size: 13px;
  margin: 7px 0 0;
}

.classic-form__links {
  display: flex;
  justify-content: space-between;
  margin: 12px 0 20px;
}

.classic-form__link {
  background: none;
  border: 0;
  color: #564495;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  padding: 0;
}

.classic-form__link:last-child {
  text-align: right;
}

.classic-form__submit {
  align-self: center;
  background: #ff7f5d;
  border: 0;
  border-radius: 24px;
  color: #fff;
  cursor: pointer;
  font: inherit;
  font-weight: 500;
  min-width: 230px;
  padding: 12px 20px;
}

.classic-form__terms {
  color: #7c7c7c;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 18px;
  text-align: center;
}

.classic-form__terms a {
  color: #564495;
  text-decoration: none;
}

.classic-form__submit:disabled,
.classic-form__link:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.classic-form--admin .classic-form__input:focus {
  border-color: #56dbcb;
}

.classic-form--admin .classic-form__submit {
  background: #56dbcb;
}
</style>
